import type { TemplateColumn } from '@thinkalpha/platform-ws-client/contracts/column-template.js';
import type { BespokeIndicatorImport } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type {
    AggregatedOutputStage as AggregatedOutputStageContract,
    AggregatedOutputColumn,
    Stage as StageContract,
    UnionStage as UnionStageContract,
    OutputStage as OutputStageContract,
    FilterStage as FilterStageContract,
    IdeaStage as IdeaStageContract,
    RiskSourceStage as RiskSourceStageContract,
    TickerSourceStage as TickerSourceStageContract,
    LegacyTableStage as LegacyTableStageContract,
    OutputColumn,
    CreateTablePayload,
} from '@thinkalpha/platform-ws-client/contracts/table.js';
import { IndicatorImportModel } from 'src/dtos/IndicatorImport';
import { IndicatorImportRefModel } from 'src/dtos/IndicatorImportRef';

export abstract class Stage {
    protected constructor(readonly bespokeIndicatorImports: BespokeIndicatorImport[] = []) {}
    abstract build(): StageContract;
}

type StageColumnInput = TemplateColumn | OutputColumn;

const NO_ADDITIONAL_SORTABLE_COLUMN_IDS: ReadonlySet<string> = new Set();

function serializeBespokeIndicatorImports(
    bespokeIndicatorImports: BespokeIndicatorImport[],
): BespokeIndicatorImport[] | undefined {
    if (!bespokeIndicatorImports.length) {
        return undefined;
    }

    return bespokeIndicatorImports.map((i) => ({
        ...i,
        formula: {
            ...i.formula,
            imports: i.formula.imports.map((ref) => {
                return ref instanceof IndicatorImportModel
                    ? ref.toRef().toContractType()
                    : ref instanceof IndicatorImportRefModel
                      ? ref.toContractType()
                      : ref;
            }),
        },
    }));
}

function intoTablePayload(
    leafStage: StageContract,
    bespokeIndicatorImports: BespokeIndicatorImport[],
    userViewKey?: CreateTablePayload['userViewKey'],
): CreateTablePayload {
    return {
        leafStage,
        userViewKey,
        bespokeIndicators: serializeBespokeIndicatorImports(bespokeIndicatorImports),
    };
}

export class SourceStage extends Stage {
    readonly stage: RiskSourceStageContract | TickerSourceStageContract | LegacyTableStageContract;

    private constructor(stage: SourceStage['stage']) {
        super();
        this.stage = stage;
    }

    static fromTickerUniverseId(universeId: string | undefined): SourceStage {
        return new SourceStage({ type: 'row-source', rowKey: 'ticker', universe: universeId });
    }

    static allForKey(rowKey: RiskSourceStageContract['rowKey'] | TickerSourceStageContract['rowKey']): SourceStage {
        return new SourceStage({ type: 'row-source', rowKey });
    }

    static forLegacyTable(tableKey: string, rowKey: string): SourceStage {
        return new SourceStage({ type: 'legacy-table', tableKey, rowKey });
    }

    override build(): RiskSourceStageContract | TickerSourceStageContract | LegacyTableStageContract {
        return this.stage;
    }

    intoFilter(
        filterStage: Omit<FilterStageContract, 'inputStage'>,
        bespokeIndicatorImports: BespokeIndicatorImport[] = [],
    ): FilterStage {
        return FilterStage.fromInputStage(this, filterStage, bespokeIndicatorImports);
    }

    intoOutput(
        stage: Omit<OutputStage['stage'], 'inputStage'>,
        templateColumns: StageColumnInput[],
        additionalSortableColumnIds: ReadonlySet<string> = NO_ADDITIONAL_SORTABLE_COLUMN_IDS,
    ) {
        return OutputStage.fromInputStage(this, stage, templateColumns, additionalSortableColumnIds);
    }

    intoAggregatedOutput(stage: Omit<AggregatedOutputStage['stage'], 'inputStage'>) {
        return AggregatedOutputStage.fromInputStage(this, stage);
    }
}

export class FilterStage extends Stage {
    readonly stage: FilterStageContract;

    private constructor(stage: FilterStage['stage'], bespokeIndicatorImports: FilterStage['bespokeIndicatorImports']) {
        super(bespokeIndicatorImports);
        this.stage = stage;
    }

    override build(): FilterStageContract {
        return this.stage;
    }

    static fromInputStage(
        source: Stage,
        stage: Omit<FilterStageContract, 'inputStage'>,
        bespokeIndicatorImports: BespokeIndicatorImport[] = [],
    ): FilterStage {
        return new FilterStage(
            {
                ...stage,
                imports: stage.imports.map((importRef) =>
                    importRef instanceof IndicatorImportModel
                        ? importRef.toRef().toContractType()
                        : importRef instanceof IndicatorImportRefModel
                          ? importRef.toContractType()
                          : importRef,
                ),
                inputStage: source.build(),
            },
            bespokeIndicatorImports,
        );
    }

    intoOutput(
        stage: Omit<OutputStage['stage'], 'inputStage'>,
        templateColumns: StageColumnInput[],
        additionalSortableColumnIds: ReadonlySet<string> = NO_ADDITIONAL_SORTABLE_COLUMN_IDS,
    ) {
        return OutputStage.fromInputStage(this, stage, templateColumns, additionalSortableColumnIds);
    }

    intoAggregatedOutput(stage: Omit<AggregatedOutputStage['stage'], 'inputStage'>) {
        return AggregatedOutputStage.fromInputStage(this, stage);
    }
}

export class IdeaStage extends Stage {
    readonly stage: IdeaStageContract;

    private constructor(stage: IdeaStage['stage'], bespokeIndicatorImports: IdeaStage['bespokeIndicatorImports']) {
        super(bespokeIndicatorImports);
        this.stage = stage;
    }

    static fromInputStage(
        inputStage: Stage,
        idea: IdeaStageContract['idea'],
        options: Omit<IdeaStageContract, 'type' | 'inputStage' | 'idea'> = {},
    ): IdeaStage {
        return new IdeaStage(
            {
                type: 'idea',
                inputStage: inputStage.build(),
                idea,
                ...options,
            },
            inputStage.bespokeIndicatorImports,
        );
    }

    override build(): IdeaStageContract {
        return this.stage;
    }

    intoOutput(
        stage: Omit<OutputStage['stage'], 'inputStage'>,
        templateColumns: StageColumnInput[],
        additionalSortableColumnIds: ReadonlySet<string> = NO_ADDITIONAL_SORTABLE_COLUMN_IDS,
    ) {
        return OutputStage.fromInputStage(this, stage, templateColumns, additionalSortableColumnIds);
    }

    intoAggregatedOutput(stage: Omit<AggregatedOutputStage['stage'], 'inputStage'>) {
        return AggregatedOutputStage.fromInputStage(this, stage);
    }
}

export class OutputStage extends Stage {
    build(): OutputStageContract {
        const { bespokeIndicatorImports, templateColumns } = this;

        const columns = (function* (): Generator<OutputColumn> {
            for (const col of templateColumns) {
                if (col.type !== 'client') {
                    yield col;
                }
            }

            for (const ind of bespokeIndicatorImports) {
                yield {
                    id: ind.id,
                    indicator: {
                        ...ind,
                        formula: {
                            ...ind.formula,
                            imports: ind.formula.imports.map((importRef) =>
                                importRef instanceof IndicatorImportModel
                                    ? importRef.toRef().toContractType()
                                    : importRef instanceof IndicatorImportRefModel
                                      ? importRef.toContractType()
                                      : importRef,
                            ),
                        },
                    },
                    type: 'indicator',
                };
            }
        })().toArray();

        const sortableColumnIds = new Set(columns.map((column) => column.id));
        for (const columnId of this.additionalSortableColumnIds) {
            sortableColumnIds.add(columnId);
        }

        const stage: OutputStageContract = {
            ...this.stage,
            sort: this.stage.sort
                ? Object.fromEntries(Object.entries(this.stage.sort).filter(([key]) => sortableColumnIds.has(key)))
                : undefined,
            type: 'output',
            columns,
        };

        return stage;
    }

    private constructor(
        stage: OutputStage['stage'],
        bespokeIndicatorImports: OutputStage['bespokeIndicatorImports'],
        templateColumns: StageColumnInput[],
        additionalSortableColumnIds: ReadonlySet<string>,
    ) {
        super(bespokeIndicatorImports);
        this.stage = stage;
        this.templateColumns = templateColumns;
        this.additionalSortableColumnIds = additionalSortableColumnIds;
    }

    static fromInputStage(
        inputStage: Stage,
        output: Omit<OutputStage['stage'], 'inputStage' | 'columns'>,
        templateColumns: StageColumnInput[],
        additionalSortableColumnIds: ReadonlySet<string> = NO_ADDITIONAL_SORTABLE_COLUMN_IDS,
    ): OutputStage {
        return new OutputStage(
            {
                inputStage: inputStage.build(),
                ...output,
            },
            inputStage.bespokeIndicatorImports,
            templateColumns,
            additionalSortableColumnIds,
        );
    }

    intoTable(userViewKey?: CreateTablePayload['userViewKey']): CreateTablePayload {
        return intoTablePayload(this.build(), this.bespokeIndicatorImports, userViewKey);
    }

    readonly templateColumns: StageColumnInput[];

    /** Sortable fields generated by the server and therefore absent from `templateColumns`. */
    readonly additionalSortableColumnIds: ReadonlySet<string>;

    readonly stage: Omit<OutputStageContract, 'type' | 'columns'>;
}

export class AggregatedOutputStage extends Stage {
    build(): AggregatedOutputStageContract {
        const columns = this.stage.columns;
        const groupByColumns = Array.isArray(this.stage.groupBy) ? this.stage.groupBy : [this.stage.groupBy];
        const sortableColumnIds = [...groupByColumns, ...columns].map((column) => column.id);

        return {
            ...this.stage,
            sort: this.stage.sort
                ? Object.fromEntries(Object.entries(this.stage.sort).filter(([key]) => sortableColumnIds.includes(key)))
                : undefined,
            type: 'aggregated-output',
            columns,
        };
    }

    private constructor(
        stage: AggregatedOutputStage['stage'],
        bespokeIndicatorImports: AggregatedOutputStage['bespokeIndicatorImports'],
    ) {
        super(bespokeIndicatorImports);
        this.stage = stage;
    }

    static fromInputStage(
        inputStage: Stage,
        output: Omit<AggregatedOutputStage['stage'], 'inputStage'>,
    ): AggregatedOutputStage {
        return new AggregatedOutputStage(
            {
                inputStage: inputStage.build(),
                ...output,
            },
            inputStage.bespokeIndicatorImports,
        );
    }

    intoTable(userViewKey?: CreateTablePayload['userViewKey']): CreateTablePayload {
        return intoTablePayload(this.build(), this.bespokeIndicatorImports, userViewKey);
    }

    readonly stage: Omit<AggregatedOutputStageContract, 'type'> & { columns: AggregatedOutputColumn[] };
}

export class UnionStage extends Stage {
    readonly stage: UnionStageContract;

    private constructor(stage: UnionStageContract, bespokeIndicatorImports: BespokeIndicatorImport[]) {
        super(bespokeIndicatorImports);
        this.stage = stage;
    }

    static from(stages: Stage[]) {
        const allBespokeIndicatorImports = stages.flatMap((stage) => stage.bespokeIndicatorImports);
        return new UnionStage(
            {
                type: 'union',
                inputStages: stages.map((stage) => stage.build()),
            },
            allBespokeIndicatorImports,
        );
    }

    override build(): UnionStageContract {
        return this.stage;
    }

    intoOutput(
        stage: Omit<OutputStage['stage'], 'inputStage'>,
        templateColumns: StageColumnInput[],
        additionalSortableColumnIds: ReadonlySet<string> = NO_ADDITIONAL_SORTABLE_COLUMN_IDS,
    ) {
        return OutputStage.fromInputStage(this, stage, templateColumns, additionalSortableColumnIds);
    }

    intoAggregatedOutput(stage: Omit<AggregatedOutputStage['stage'], 'inputStage'>) {
        return AggregatedOutputStage.fromInputStage(this, stage);
    }
}
